'use client';

import { RepeatIcon } from '@/_assets/icon-components';
import PhoneOrder from '@/_assets/icon-components/phone-order';
import { ISingleOrder } from '@/_service/types/orders';
import { IRedux } from '@/_store/types';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Link } from '@/i18n/navigation';
import { getBrandHref } from '@/_lib/brand-href';
import {
  Box,
  Center,
  Container,
  Flex,
  HStack,
  SimpleGrid,
  Stack,
  StackSeparator,
  Text,
  Textarea,
} from '@chakra-ui/react';
import { Rating, RoundedStar } from '@smastrom/react-rating';
import { useTranslations } from 'next-intl';
import dynamic from 'next/dynamic';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import StepStatus from '../../StepStatus';
import OrderDetails from '../../components/OrderDetail';
import ProductCard from '../../components/ProductCard';
import CourierMap from '../desktop/map';
import OrderInfo from '../desktop/order-info';

const UniversalHeader = dynamic(() => import('@/_components/Header'));

const PreviewMobile = ({
  id,
  sent,
  order,
  total,
  change,
  status,
  refetch,
  onSubmitReview,
  renderStatusText,
}: {
  id: string;
  total: number;
  change: number;
  sent: boolean;
  order: ISingleOrder;
  status: string;
  refetch: () => void;
  onSubmitReview: (payload: { rate: number; reviewMsg: string }) => void;
  renderStatusText: () => React.ReactElement;
}) => {
  const t = useTranslations();
  const [rate, setRate] = useState(5);
  const [reviewMsg, setReviewMsg] = useState('');
  const [showMore, setShowMore] = useState(true);

  const { data: shipperId } = useSelector((state: IRedux) => state.settings);

  return (
    <Flex direction='column' bg='background' gap='2' pb='90px'>
      <Box bg='white' borderBottomRadius='xl'>
        <UniversalHeader
          backLink='/orders'
          title={`${t('order')} №${order?.external_order_id}`}
        />
        <Container py='4'>
          <OrderInfo order={order} total={total} />

          <Center flexDir='column'>
            <StepStatus order={order} />

            <Center>
              {(status === 'finished' || status === 'delivered') &&
                renderStatusText()}
            </Center>

            <Box mt='4'>
              {((order.payment_type === 'online' && !order.paid) ||
                (status !== 'cancelled' && status !== 'finished')) &&
                status !== 'finished' &&
                status !== 'delivered' &&
                status !== 'cancelled' &&
                renderStatusText()}
            </Box>
          </Center>

          {status === 'courier-picked' && (
            <Box
              position='relative'
              w='full'
              transition='all 300ms ease-in-out'
              overflow='hidden'
              zIndex={1}
              h='200px'
              mt='2'
              py='2'
              borderTop='1px solid'
              borderColor='background'
            >
              <CourierMap order={order} />
            </Box>
          )}

          <Center w={'full'} mt={3}>
            <SimpleGrid
              w={'full'}
              columns={
                1 +
                Number(Boolean(order?.courier?.phone)) +
                Number(status === 'delivered' || status === 'finished')
              }
              gap={3}
              py={3}
            >
              {order?.courier?.phone && (
                <Link href={'tel:' + order?.courier?.phone}>
                  <Button
                    w='full'
                    color={'text'}
                    fontSize='12px'
                    height={'66px'}
                    fontWeight={400}
                    bg={'background'}
                    variant={'subtle'}
                  >
                    <Stack align='center' gap={1}>
                      <PhoneOrder />
                      <Text fontSize='12px'>{t('call_the_courier')}</Text>
                    </Stack>
                  </Button>
                </Link>
              )}
              <Link
                href={'tel:+998712008989'}
                target={shipperId?.data?.use_support_link ? '_blank' : '_self'}
              >
                <Button
                  w='full'
                  fontWeight={400}
                  variant='subtle'
                  bg='background'
                  h='64px'
                >
                  <Stack align='center' gap={2} py='4'>
                    <PhoneOrder />
                    <Text color='text' fontSize='12px'>
                      71 200 89 89
                    </Text>
                  </Stack>
                </Button>
              </Link>
              {(status === 'delivered' || status === 'finished') && (
                <Link
                  href={getBrandHref(order?.shipper_id, {
                    path: '/checkout',
                    query: { repeat: 'true', oid: id },
                  })}
                >
                  <Button
                    w='full'
                    color={'text'}
                    height={'66px'}
                    fontSize='12px'
                    fontWeight={400}
                    bg={'background'}
                    variant={'subtle'}
                  >
                    <Stack align='center' gap={1}>
                      <RepeatIcon />
                      <Text fontSize='xs'>{t('repeat')}</Text>
                    </Stack>
                  </Button>
                </Link>
              )}
            </SimpleGrid>
          </Center>
        </Container>
      </Box>

      <Box bg='white' rounded='xl' pt='6'>
        <Container>
          <Text fontSize='lg' lineHeight='24px' fontWeight={700}>
            {t('order_composition')}
          </Text>
        </Container>

        <Stack px={4} separator={<StackSeparator />} gap={0}>
          {order?.steps?.[0].products.map((product) => (
            <ProductCard
              refetch={refetch}
              key={product.id}
              product={product}
              orderData={order}
            />
          ))}
        </Stack>

        <Box px='4' pb='4'>
          {order?.description && (
            <Flex
              flexDir={'column'}
              gap={3}
              bg={'background'}
              p={4}
              rounded={16}
            >
              <Text fontSize={'sm'} fontWeight={500}>
                {t('description_to_the_order')}
              </Text>
              <Text fontSize={'sm'} fontWeight={400}>
                {order?.description}
              </Text>
            </Flex>
          )}
        </Box>
      </Box>

      <Box bg='white' rounded='xl' pt='6'>
        <OrderDetails order={order} total={total} change={change} />
      </Box>

      <Box>
        {sent ? (
          <Alert status='success' title={t('successfully_sent')} />
        ) : (
          (status === 'delivered' || status === 'finished') && (
            <Container bg='white' rounded='xl' pt='6' pb='4'>
              <HStack
                align='center'
                justify='space-between'
                mb={!order?.review ? 3 : 0}
              >
                <Text fontSize='lg' fontWeight={700}>
                  {t('rate')}
                </Text>
                {!order?.review && (
                  <Rating
                    readOnly={Boolean(order?.review_id)}
                    style={{ maxWidth: 100 }}
                    value={!order?.review_id ? rate : order?.rating}
                    itemStyles={{
                      itemShapes: RoundedStar,
                      activeFillColor: '#FFC500',
                      inactiveFillColor: '#E0E8F1',
                    }}
                    onChange={(newValue: number) => {
                      setRate(newValue);
                    }}
                  />
                )}
              </HStack>

              <Stack>
                {!order?.review_id ? (
                  <Stack gap={4} color={'text'}>
                    <Textarea
                      size={'xl'}
                      rounded={16}
                      height={'100px'}
                      variant='subtle'
                      bgColor={'background'}
                      placeholder={t('describe_your_impressions')}
                      value={reviewMsg}
                      onChange={(e) => setReviewMsg(e.target.value)}
                    />

                    <Button
                      mt={4}
                      onClick={() =>
                        onSubmitReview({
                          rate,
                          reviewMsg,
                        })
                      }
                      bg='primary'
                      color='primary.text'
                      _hover={{ bg: 'primary.hover' }}
                      size={'2xl'}
                      height={'56px'}
                      disabled={rate < 1}
                      loading={sent}
                    >
                      {t('send')}
                    </Button>
                  </Stack>
                ) : (
                  // <Text>{order?.review}</Text>
                  <Stack p={2} mt={2} borderRadius={8} bg={'#F9FAFB'}>
                    <Text fontSize={14}>{t('your_feedback')}</Text>
                    {/* <Text>{product?.review?.rating}</Text> */}
                    <Rating
                      readOnly={Boolean(order?.review)}
                      style={{ maxWidth: 100 }}
                      value={!order?.review_id ? rate : order?.rating}
                      itemStyles={{
                        itemShapes: RoundedStar,
                        activeFillColor: '#FFC500',
                        inactiveFillColor: '#E0E8F1',
                      }}
                    />
                    <Text mt={'2px'} fontSize={12} lineHeight={'17px'}>
                      {showMore
                        ? order?.review
                        : order?.review?.substring(0, 140) + '...'}
                    </Text>
                    {order?.review?.length > 140 && (
                      <Button
                        w='fit-content'
                        color={'primary.500'}
                        fontSize='xs'
                        fontWeight={400}
                        transform={`translateY(${showMore ? 0 : -5}px)`}
                        onClick={() => setShowMore((prev) => !prev)}
                      >
                        {showMore ? t('show_less') : t('show_more')}
                      </Button>
                    )}
                  </Stack>
                )}
              </Stack>
            </Container>
          )
        )}
      </Box>

      {status !== 'finished' &&
        status !== 'delivered' &&
        status !== 'cancelled' &&
        order?.payment_link && (
          <Box
            position={'fixed'}
            w={'full'}
            zIndex={99}
            py={4}
            bottom={0}
            bg={'background.card'}
          >
            <Container>
              <Link href={order?.payment_link}>
                <Button
                  w={'full'}
                  bg='primary'
                  color='primary.text'
                  _hover={{ bg: 'primary.hover' }}
                  size={'xl'}
                  h={'56px'}
                >
                  {t('to_pay')}
                </Button>
              </Link>
            </Container>
          </Box>
        )}
    </Flex>
  );
};

export default PreviewMobile;
